import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
} from '@angular/core';

import { UrlFieldValidators } from 'src/app/components/url-field/url-field-validators';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { OAuthService } from 'src/app/components/content-oauth/services/oauth.service';
import { AnalyticsService } from 'src/app/components/logging/analytics.service';
import { DocumentService } from 'src/app/storage/services/document.service';
import { environment } from 'src/environments/environment';

import { AttributeDataService } from '../../services/attribute-data.service';
import { ComponentsService } from '../../services/components.service';
import { SlidesUrlValidationServiceService } from '../services/slides-url-validation-service.service';
import { ComponentUtilsService } from '../services/component-utils.service';
import { GoogleService } from '../services/google.service';
import { GoogleSlidesService } from '../services/google-slides.service';
import { GoogleDrivePickerService } from '../services/google-drive-picker.service';

const PROVIDER_GOOGLE_SLIDES = 'google';
const SCOPE_GOOGLE_DRIVE_PICKER = 'https://www.googleapis.com/auth/drive.file';
const ASSET_TYPE_GOOGLE_SLIDES_IMAGES = 'googleSlidesImages';
const GOOGLE_SLIDES_MIME_TYPE = 'application/vnd.google-apps.presentation';

@Component({
  selector: 'template-component-slides',
  templateUrl: './slides.component.html',
  styleUrls: ['./slides.component.scss'],
  standalone: false,
})
export class SlidesComponent {
  spinner = false;
  componentId;
  src;
  duration;
  validationResult;

  // Google Drive ("private deck") state
  provider: string | null = null;
  componentAccount: string | null = null;
  componentAccountUsername: string | null = null;
  fileId: string | null = null;
  fileName: string | null = null;
  assetCount: number | null = null;
  driveConnecting = false;
  driveError = false;
  fileAccessFailed = false;
  conversionError = '';
  isDropdownOpen = false;
  // Bumped on every reload (_load()) or mode switch (switchSource()) of
  // this componentId, so a selectFromDrive() flow spanning either can tell
  // its own result is stale even though _isCurrent(componentId) alone still
  // matches (the component wasn't switched away from, just reloaded or
  // switched to URL mode and back).
  private driveFlowGeneration = 0;
  // DebounceDirective emits on every ngModel valueChanges after the first,
  // not just ones caused by the user typing — a programmatic `this.src =`
  // assignment (from _load(), reset(), or selectFromDrive()) fires it too.
  // Track whether the most recent src change came from the input element
  // itself so the debounced callback (bound in the template) can tell
  // genuine edits from those.
  srcUserEdited = false;

  constructor(
    private elementRef: ElementRef,
    private componentsFactory: ComponentsService,
    private attributeDataFactory: AttributeDataService,
    private slidesUrlValidationService: SlidesUrlValidationServiceService,
    private changeDetectorRef: ChangeDetectorRef,
    private oAuthService: OAuthService,
    private componentUtilsService: ComponentUtilsService,
    private googleService: GoogleService,
    private googleSlidesService: GoogleSlidesService,
    private googleDrivePickerService: GoogleDrivePickerService,
    private documentService: DocumentService,
    private companyStateService: CompanyStateService,
    private analyticsFactory: AnalyticsService,
  ) {
    componentsFactory.registerDirective({
      type: 'rise-slides',
      element: this.elementRef.nativeElement,
      show: () => {
        this.componentId = this.componentsFactory.selected.id;
        this._load();
        if (this.provider !== PROVIDER_GOOGLE_SLIDES) {
          this.saveSrc(); //validate Slides URL
        }
      },
    });
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange() {
    if (
      this._directiveIsVisible() &&
      !document.hidden &&
      this.provider !== PROVIDER_GOOGLE_SLIDES
    ) {
      this.saveSrc();
    }
  }

  async _load() {
    this.srcUserEdited = false;
    this.src = this.attributeDataFactory.getAvailableAttributeData(
      this.componentId,
      'src',
    );
    this.duration =
      parseInt(
        this.attributeDataFactory.getAvailableAttributeData(
          this.componentId,
          'duration',
        ),
      ) || 10;

    this.provider =
      this.attributeDataFactory.getAvailableAttributeData(
        this.componentId,
        'provider',
      ) || null;
    this.componentAccount = this.attributeDataFactory.getAvailableAttributeData(
      this.componentId,
      'account',
    );
    this.componentAccountUsername = null;
    // Mirrors DocumentComponent's Rise Storage guard (source !== original):
    // only treat the asset count as stale when the underlying file actually
    // changed, not on every reopen of the panel — otherwise reopening an
    // already-converted deck redundantly re-polls for up to 60s and can
    // surface a misleading "couldn't be converted" error even though
    // conversion already succeeded.
    const previousDriveFileId = this.fileId;
    this.fileId = this.attributeDataFactory.getAvailableAttributeData(
      this.componentId,
      'fileId',
    );
    this.fileName = this.attributeDataFactory.getAvailableAttributeData(
      this.componentId,
      'fileName',
    );
    if (this.fileId !== previousDriveFileId) {
      this.assetCount = null;
    }
    this.conversionError = '';
    this.driveError = false;
    // Reset transient flow state on every (re)selection — driveConnecting,
    // spinner and isDropdownOpen are shared on this singleton, so a pending
    // selectFromDrive() (whose `finally` only clears them when its
    // componentId is still current) or a stuck-open file-options menu
    // shouldn't bleed into a different component's selection.
    this.driveConnecting = false;
    this.spinner = false;
    this.isDropdownOpen = false;
    this.driveFlowGeneration++;

    if (this.provider === PROVIDER_GOOGLE_SLIDES && this.fileId) {
      if (!this.assetCount) {
        this._refreshAssetCount(
          this.componentId,
          this.componentAccount,
          this.fileId,
        );
      }
    }

    if (this.provider === PROVIDER_GOOGLE_SLIDES) {
      this.spinner = true;
      this.fileAccessFailed = false;
      // Captured up front, like _refreshAssetCount's args above: this.componentId
      // may change while we're awaiting below, and the check inside
      // _validateFileAccess must compare against the target this call started
      // with, not whatever is current by the time it runs.
      const componentId = this.componentId;
      const account = this.componentAccount;
      const fileId = this.fileId;

      try {
        await Promise.all([
          this._validateCredentials(),
          fileId
            ? this._validateFileAccess(componentId, account, fileId)
            : Promise.resolve(),
        ]);
      } finally {
        this.spinner = false;
        this.changeDetectorRef.detectChanges();
      }
    }
  }

  saveDuration() {
    this._saveDriveAttributeData();
  }

  onSrcInput(): void {
    this.srcUserEdited = true;
  }

  saveSrc(userInitiated = false) {
    if (this._validateSrcLocally()) {
      this.attributeDataFactory.setAttributeData(
        this.componentId,
        'src',
        this.src,
      );

      this.spinner = true;

      const componentId = this.componentId;
      const src = this.src;

      this.slidesUrlValidationService
        .validate(this.src)
        .then((result) => {
          // Bail out if the user switched to a different rise-slides
          // component, or changed/cleared this component's src (e.g. by
          // switching to the Drive picker), while this check was pending —
          // same class of race the Drive path guards with _isCurrent().
          if (!this._isCurrent(componentId) || this.src !== src) {
            return;
          }

          this.validationResult = result;

          // Only a user-initiated save (the debounced URL input) of a URL
          // the remote check confirms VALID represents an actual playable
          // public-deck adoption — passing the local regex isn't enough
          // (NOT_PUBLIC/DELETED decks pass it too), and saveSrc() is also
          // called from show()/onVisibilityChange() to silently re-validate
          // an already-saved URL, which must not inflate the count either.
          if (userInitiated && src && result === 'VALID') {
            this._track('public');
          }
        })
        .finally(() => {
          this.spinner = false;
          this.changeDetectorRef.detectChanges();
        });
    }
    this.changeDetectorRef.detectChanges();
  }

  async selectFromDrive() {
    const componentId = this.componentId;
    const flowGeneration = this.driveFlowGeneration;

    this.driveError = false;
    this.driveConnecting = true;

    this.spinner = true;

    try {
      const authResult: any = await this._connectAccount(
        PROVIDER_GOOGLE_SLIDES,
        SCOPE_GOOGLE_DRIVE_PICKER,
      );

      // Keep the authenticated account in locals rather than on `this` until
      // a file is actually picked — otherwise a cancelled re-auth (e.g. the
      // user picks a different Google account, then cancels the picker)
      // would leave the displayed account mismatched with the still-unchanged
      // fileId/fileName from the previous selection.
      const account = this.oAuthService.getUserIdentifier(
        PROVIDER_GOOGLE_SLIDES,
      );
      // Only reuse the previously-displayed username as a fallback when it
      // actually belongs to this account — otherwise (e.g. the user
      // re-authenticated as a different Google account and this particular
      // response didn't carry a username) we'd show the old account's email
      // under the new account.
      const accountUsername =
        authResult?.username ||
        (account === this.componentAccount
          ? this.componentAccountUsername
          : null);

      const signature = await this.componentUtilsService.getSignature(
        account,
        PROVIDER_GOOGLE_SLIDES,
      );
      const accessToken = await this.googleService.getToken(
        account,
        PROVIDER_GOOGLE_SLIDES,
        signature,
      );

      const selectedFile: any = await this.googleDrivePickerService.open(
        accessToken,
        'presentations',
        GOOGLE_SLIDES_MIME_TYPE,
      );

      // Bail out if the user switched to a different rise-slides component,
      // or reloaded/switched this same component's source, while this flow
      // was pending — applying the result now would corrupt whichever
      // component/mode is actually active at this point.
      if (
        selectedFile &&
        this.driveFlowGeneration === flowGeneration &&
        this._isCurrent(componentId)
      ) {
        this.provider = PROVIDER_GOOGLE_SLIDES;
        this.componentAccount = account;
        this.componentAccountUsername = accountUsername;
        this.fileId = selectedFile.id;
        this.fileName = selectedFile.name;
        this.assetCount = null;
        this.conversionError = '';
        this.fileAccessFailed = false;
        this.validationResult = '';
        // The player only reads `src` — clear any previously-set public URL
        // so a stale public deck doesn't keep playing once Drive is connected.
        this.srcUserEdited = false;
        this.src = '';

        await this._convertAndCount(componentId, account, selectedFile.id);
      }
    } catch (error) {
      console.error('[Slides] Failed to connect/select from Drive', error);
      if (
        this.driveFlowGeneration === flowGeneration &&
        this._isCurrent(componentId)
      ) {
        this.driveError = true;
      }
    } finally {
      if (
        this.driveFlowGeneration === flowGeneration &&
        this._isCurrent(componentId)
      ) {
        // save the attribute data after conversion is complete
        // that way component will re-render content after conversion is complete
        this._saveDriveAttributeData();

        this.driveConnecting = false;
        this.spinner = false;
      }
      this.changeDetectorRef.detectChanges();
    }
  }

  changeFile() {
    return this.selectFromDrive();
  }

  onDropdownOpen() {
    this.isDropdownOpen = true;
  }

  onDropdownClose() {
    this.isDropdownOpen = false;
  }

  switchSource(): void {
    this.isDropdownOpen = false;
    this.reset();
    this._saveDriveAttributeData();
  }

  reset(): void {
    this.driveFlowGeneration++;
    this.driveConnecting = false;
    this.spinner = false;
    this.provider = null;
    this.componentAccount = null;
    this.componentAccountUsername = null;
    this.fileId = null;
    this.fileName = null;
    this.assetCount = null;
    this.conversionError = '';
    this.driveError = false;
    this.fileAccessFailed = false;
    this.srcUserEdited = false;
    this.src = '';
    this.validationResult = '';
  }

  private async _convertAndCount(
    componentId: string,
    account: string,
    fileId: string,
  ) {
    try {
      await this.googleSlidesService.subscribe(
        account,
        fileId,
        this.companyStateService.getSelectedCompanyId(),
      );
      if (!this._isCurrent(componentId, fileId, account)) {
        return;
      }
      await this._getAssetCount();
      if (!this._isCurrent(componentId, fileId, account)) {
        return;
      }
      // Track adoption once conversion has fully succeeded (subscribe, then
      // asset count), matching DocumentComponent's subscribe -> asset count
      // -> save -> track order. A failed subscribe() or asset-count fetch
      // surfaces the same "couldn't be converted" error to the user (below),
      // so neither should count as adoption.
      this._track('private');
    } catch (error) {
      console.error('[Slides] Failed to convert Google Slides file', error);
      if (this._isCurrent(componentId, fileId, account)) {
        this.conversionError = "Your Google Slides file couldn't be converted.";
      }
    }
  }

  // SlidesComponent is a singleton reused across every rise-slides element in
  // the template — `this.componentId` is reassigned on every selection, and
  // `this.componentAccount` changes when the user re-authenticates as a different
  // Google account on the same component. A pending async call must re-check
  // its target is still selected/active before applying its result (or a
  // race would apply/persist one component's — or one account's — data onto
  // whichever component/account is now active once it resolves).
  private _isCurrent(
    componentId: string,
    fileId?: string,
    account?: string,
  ): boolean {
    if (this.componentId !== componentId) {
      return false;
    }
    if (account !== undefined && this.componentAccount !== account) {
      return false;
    }
    return fileId === undefined || this.fileId === fileId;
  }

  private async _refreshAssetCount(
    componentId: string,
    account: string,
    fileId: string,
  ) {
    try {
      await this._getAssetCount();
      if (!this._isCurrent(componentId, fileId, account)) {
        return;
      }
      this.changeDetectorRef.detectChanges();
    } catch {
      if (!this._isCurrent(componentId, fileId, account)) {
        return;
      }
      this.conversionError = "Your Google Slides file couldn't be converted.";
      this.changeDetectorRef.detectChanges();
    }
  }

  private async _validateFileAccess(
    componentId: string,
    account: string,
    fileId: string,
  ) {
    try {
      await this.googleSlidesService.getFileInfo(account, fileId);
    } catch {
      if (this._isCurrent(componentId, fileId, account)) {
        this.fileAccessFailed = true;
      }
    }
  }

  private _connectAccount(provider: string, scope: string) {
    return this.oAuthService.getConnectionStatus(provider, scope).catch(() => {
      return this.oAuthService.authenticate(provider, scope);
    });
  }

  private async _validateCredentials() {
    // Captured up front: this component is a singleton reused across every
    // rise-slides element, so the selection can change before the fetch
    // resolves.
    const componentId = this.componentId;
    const account = this.componentAccount;

    try {
      // get username of a user who configured the component
      const { username } = await this.oAuthService.getUsername(
        PROVIDER_GOOGLE_SLIDES,
        account,
      );

      if (this._isCurrent(componentId, undefined, account)) {
        this.componentAccountUsername = username;
      }
    } catch {
      console.log('Failed to get username');
    }
  }

  private async _getAssetCount() {
    const componentId = this.componentId;
    const account = this.componentAccount;
    const fileId = this.fileId;

    const count = await this.documentService.getAssetCount(
      `${account}_${fileId}`,
      ASSET_TYPE_GOOGLE_SLIDES_IMAGES,
      this.companyStateService.getSelectedCompanyId(),
    );

    if (this._isCurrent(componentId, fileId, account)) {
      this.assetCount = count;
    }
  }

  private _saveDriveAttributeData() {
    this.attributeDataFactory.setAttributeData(
      this.componentId,
      'environment',
      environment.production ? 'prod' : 'test',
    );
    this.attributeDataFactory.setAttributeData(
      this.componentId,
      'provider',
      this.provider,
    );
    this.attributeDataFactory.setAttributeData(
      this.componentId,
      'account',
      this.componentAccount,
    );
    this.attributeDataFactory.setAttributeData(
      this.componentId,
      'fileId',
      this.fileId,
    );
    this.attributeDataFactory.setAttributeData(
      this.componentId,
      'fileName',
      this.fileName,
    );
    // `src` is the only attribute the player reads for playback, so it must
    // always be kept in sync with the Drive/URL mode switch, in both
    // directions — otherwise a stale value from the other mode lingers.
    this.attributeDataFactory.setAttributeData(
      this.componentId,
      'src',
      this.src,
    );
    if (this.duration > 0) {
      this.attributeDataFactory.setAttributeData(
        this.componentId,
        'duration',
        this.duration,
      );
    }
  }

  private _track(provider: 'private' | 'public') {
    this.analyticsFactory.track('Slides Component Updated', {
      companyId: this.companyStateService.getSelectedCompanyId(),
      provider,
    });
  }

  _directiveIsVisible() {
    // This directive is instantiated once by templateAttributeEditor
    // It becomes visible when <rise-slides> is selected
    return (
      this.componentsFactory.selected &&
      this.componentsFactory.selected.type === 'rise-slides'
    );
  }

  _validateSrcLocally() {
    //clear the error
    this.validationResult = '';

    var PUBLISHED_URL_REGEXP =
      /^(http:|https:)\/\/docs\.google\.com\/presentation\/d\/e\/([^\s]+)\/(pub|embed)(\?|$)/i;
    var BROWSER_URL_REGEXP =
      /^(http:|https:)\/\/docs\.google\.com\/presentation\/d\/([\w-_]+)/i;

    var _src = !this.src ? '' : this.src.trim();
    if (_src === '') {
      //empty string is allowed
      return true;
    }

    if (UrlFieldValidators.isInsecureHttpUrl(_src)) {
      this.src = _src.replace('http://', 'https://');
    }

    //check if uses entered Published URL
    if (PUBLISHED_URL_REGEXP.test(_src)) {
      //nothing to do here - URL is formatted correctly
      return true;
    }

    //check if uses entered URL from browser navigation bar
    if (BROWSER_URL_REGEXP.test(_src)) {
      var slideId = BROWSER_URL_REGEXP.exec(_src)[2];
      this.src = this._slideIdToEmbedUrl(slideId);
      return true;
    }

    //check if uses entered Slide ID - there should be no occurrences of '/' in the string
    if (_src.indexOf('/') === -1) {
      this.src = this._slideIdToEmbedUrl(_src);
      return true;
    }

    this.validationResult = 'INVALID';
    return false;
  }

  _slideIdToEmbedUrl(slideId) {
    // There are 2 types of Slide ID:
    //  1) Google doc ID i.e. ID copied from the browser's navigation bar
    //  2) ID of the published URL. This ID ususally starst with "2PACX-"
    // For this implementaion we assume user entered Google doc ID (option #1)
    return 'https://docs.google.com/presentation/d/' + slideId + '/embed';
  }
}
